import { useAction } from "convex/react"
import { useCallback, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { useSolanaSign } from "@/hooks/useSolanaSign"
import { useWebBalance } from "@/hooks/useWebBalance"
import { getErrorMessage } from "@/lib/errors"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import { MICRO_USDC } from "~shared/constants"
import { Spinner } from "./Spinner"

interface PayModalProps {
	onClose: () => void
	recipientDisplayName: string
	recipientHandle: string
	recipientUserId: Id<"users">
}

export function PayModal({
	onClose,
	recipientDisplayName,
	recipientHandle,
	recipientUserId,
}: PayModalProps) {
	const [amount, setAmount] = useState("")
	const [submitting, setSubmitting] = useState(false)
	const submittingRef = useRef(false)

	const { usdcBalance, refetchUSDC } = useWebBalance()
	const { isReady: isWalletReady, signTransaction } = useSolanaSign()
	const prepareSend = useAction(api.send.prepareSendUsdc)
	const commitSend = useAction(api.send.commitSendUsdc)

	const parsedAmount = Math.floor((Number.parseFloat(amount) || 0) * 100) / 100
	const microAmount = Math.round(parsedAmount * MICRO_USDC)
	const maxAmount = usdcBalance / MICRO_USDC
	const canSubmit = parsedAmount >= 1 && microAmount <= usdcBalance && isWalletReady && !submitting

	const executeSend = useCallback(async () => {
		if (submittingRef.current || microAmount <= 0) {
			return
		}
		submittingRef.current = true
		setSubmitting(true)

		try {
			const prepared = await prepareSend({ recipientUserId, microAmount })
			const signedTx = await signTransaction(prepared.tx)
			await commitSend({ prepare_token: prepared.prepareToken, signed_tx: signedTx })
			toast.success(`Sent $${parsedAmount.toFixed(2)} to @${recipientHandle}`)
			refetchUSDC()
			setAmount("")
			onClose()
		} catch (e: unknown) {
			toast.error(getErrorMessage(e))
		} finally {
			submittingRef.current = false
			setSubmitting(false)
		}
	}, [
		commitSend,
		microAmount,
		onClose,
		parsedAmount,
		prepareSend,
		recipientHandle,
		recipientUserId,
		refetchUSDC,
		signTransaction,
	])

	const handleConfirm = useCallback(() => {
		if (!canSubmit || submittingRef.current) {
			return
		}
		executeSend().catch(() => {})
	}, [canSubmit, executeSend])

	const handleMax = useCallback(() => {
		if (maxAmount >= 1) {
			setAmount(maxAmount.toFixed(2))
		}
	}, [maxAmount])

	return createPortal(
		<dialog
			aria-label={`Pay @${recipientHandle}`}
			className="fixed inset-0 z-50 flex h-full max-h-full w-full max-w-full animate-fade-in items-center justify-center border-0 bg-black/70 open:flex"
			onClick={onClose}
			onKeyDown={e => {
				if (e.key === "Escape") {
					onClose()
				}
			}}
			open
		>
			{/* biome-ignore lint/a11y/noStaticElementInteractions: prevents backdrop click-through */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled by dialog parent */}
			<div
				className="w-[95vw] max-w-sm animate-slide-up rounded-2xl border border-app-border bg-app-surface p-6 shadow-2xl"
				onClick={e => e.stopPropagation()}
			>
				<div className="mb-4 text-center">
					<h2 className="font-bold text-app-text text-xl">Pay @{recipientHandle}</h2>
					<p className="text-app-text-secondary text-sm">{recipientDisplayName}</p>
				</div>

				<div className="mb-2 flex items-center gap-2 rounded-xl border border-app-border bg-app-surface-light px-4 py-3">
					<span className="font-bold text-2xl text-app-text-secondary">$</span>
					<input
						autoFocus
						className="min-w-0 flex-1 bg-transparent font-bold text-2xl text-app-text outline-none placeholder:text-app-text-tertiary"
						disabled={submitting}
						inputMode="decimal"
						onChange={e => setAmount(e.target.value)}
						placeholder="0.00"
						type="text"
						value={amount}
					/>
					<span className="font-semibold text-app-text-secondary text-sm">USDC</span>
				</div>

				<div className="mb-4 text-right">
					<button
						className="text-app-text-tertiary text-xs transition-colors hover:text-app-text"
						onClick={handleMax}
						type="button"
					>
						Available: ${maxAmount.toFixed(2)} USDC
					</button>
				</div>

				{parsedAmount > 0 && parsedAmount < 1 && (
					<p className="mb-3 text-center text-red-400 text-sm">Minimum $1.00</p>
				)}
				{microAmount > usdcBalance && parsedAmount >= 1 && (
					<p className="mb-3 text-center text-red-400 text-sm">Insufficient balance</p>
				)}

				<button
					className={`w-full rounded-full bg-white py-2.5 font-bold text-base text-black transition-opacity ${
						canSubmit ? "hover:opacity-90" : "cursor-not-allowed opacity-40"
					}`}
					disabled={!canSubmit}
					onClick={handleConfirm}
					type="button"
				>
					{submitting ? (
						<Spinner />
					) : (
						`Send $${parsedAmount >= 1 ? parsedAmount.toFixed(2) : "0.00"} USDC`
					)}
				</button>

				<button
					className="mt-3 w-full py-2 text-app-text-tertiary text-sm transition-colors hover:text-app-text"
					onClick={onClose}
					type="button"
				>
					Cancel
				</button>
			</div>
		</dialog>,
		document.body,
	)
}
